import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { MdContentCopy, MdInfoOutline } from 'react-icons/md'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../lib/http'
import * as Table from '../../claim-history/simple-table'
import { Copyable } from '../../layout/sidebar'
import { ContextMenuButton } from '../../shared/ContextMenu'
import FriendlyDate from "../../shared/Date/FriendlyDate"
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'

interface MatchedUser {
    id: string
    email: string
}

interface Group {
    number: string
    name: string
}

interface GroupsApiResponse {
    ok: boolean

    data: {
        groups: Group[]

        meta: {
            total: number
        }
    }
}

export interface PortalPatient extends Table.Item {
    id: string
    card_id: string
    member_id: string
    participant_id: string
    name: string
    group: Group
    state: string
    relationship: 'enrollee' | 'dependent'
    active: boolean
    birth_date: string
    benefit_plans: string[]
    matched_users: MatchedUser[]
}

interface ApiResponse {
    ok: boolean
    data: {
        patients: PortalPatient[]
        meta: {
            total: number
        }
    }
}

const clearButtonStyle: React.CSSProperties = {
    color: '#7826f5',
    backgroundColor: 'rgba(220, 197, 249, 0.5)',
    backdropFilter: 'blur(9px)',

    textTransform: 'uppercase',
    fontSize: '0.75rem',
    fontWeight: '600',
    textAlign: 'center',

    padding: '0.5rem',
    width: '100%',

    borderBottomLeftRadius: '1rem',
    borderBottomRightRadius: '1rem',

    cursor: 'pointer'
}

export function PortalIndex(): JSX.Element {
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    const syncingFromURL = useRef(false);

    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [searchQuery, setSearchQuery] = useState<string | undefined>(urlSearchParams.get('query') || undefined)

    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
    const [relationshipFilter, setRelationshipFilter] = useState<string | undefined>(undefined)
    const [stateFilter, setStateFilter] = useState<string | undefined>(undefined)
    const [cardIdFilter, setCardIdFilter] = useState<string | undefined>(undefined)
    const [memberIdFilter, setMemberIdFilter] = useState<string | undefined>(undefined)
    const [benefitPlanFilter, setBenefitPlanFilter] = useState<string | undefined>(undefined)
    const [matchedUserEmailFilter, setMatchedUserEmailFilter] = useState<string | undefined>(undefined)
    const [onlyMatchedUsersFilter, setOnlyMatchedUsersFilter] = useState<string | undefined>(undefined)
    const [nameFilter, setNameFilter] = useState<string | undefined>(undefined)
    const [birthDateFilter, setBirthDateFilter] = useState<string | undefined>(undefined)
    const [groupsFilter, setGroupsFilter] = useState<string[]>([])

    const [sort, setSort] = useState<Table.Sort<PortalPatient>>({field: 'id', direction: 'asc' })

    const [debouncedQuery] = useDebounceValue(searchQuery, 600)
    const [debouncedStatus] = useDebounceValue(statusFilter, 400)
    const [debouncedRelationship] = useDebounceValue(relationshipFilter, 400)
    const [debouncedState] = useDebounceValue(stateFilter, 600)
    const [debouncedCardId] = useDebounceValue(cardIdFilter, 600)
    const [debouncedMemberId] = useDebounceValue(memberIdFilter, 600)
    const [debouncedBenefitPlan] = useDebounceValue(benefitPlanFilter, 600)
    const [debouncedMatchedUserEmail] = useDebounceValue(matchedUserEmailFilter, 600)
    const [debouncedName] = useDebounceValue(nameFilter, 600)
    const [debouncedBirthDate] = useDebounceValue(birthDateFilter, 400)

    useEffect(() => {
        const urlQuery = urlSearchParams.get('query') || undefined
        if (urlQuery !== searchQuery) {
            syncingFromURL.current = true
            setSearchQuery(urlQuery)
            setPage(0)
        }
    }, [urlSearchParams])

    useEffect(() => {
        if (syncingFromURL.current) {
            syncingFromURL.current = false
            return
        }

        const urlQuery = urlSearchParams.get('query') || undefined
        const newQuery = searchQuery || undefined
        if (urlQuery !== newQuery) {
            const newParams = new URLSearchParams()
            if (searchQuery) {
                newParams.set('query', searchQuery)
            }
            setUrlSearchParams(newParams, { replace: true })
        }
    }, [searchQuery, setUrlSearchParams])

    const query = useQuery<ApiResponse>({
        queryKey: ['portal-index', {
            page,
            pageSize,
            query: debouncedQuery,
            statusFilter: debouncedStatus,
            relationshipFilter: debouncedRelationship,
            stateFilter: debouncedState,
            cardIdFilter: debouncedCardId,
            memberIdFilter: debouncedMemberId,
            benefitPlanFilter: debouncedBenefitPlan,
            matchedUserEmailFilter: debouncedMatchedUserEmail,
            onlyMatchedUsersFilter: onlyMatchedUsersFilter,
            nameFilter: debouncedName,
            birthDate: debouncedBirthDate,
            groups: groupsFilter,
            sort
        }],
        queryFn: async () => {
            const searchParams = new URLSearchParams([
                ['page', (page + 1).toString()],
                ['pageSize', pageSize.toString()],
            ] as any)

            if (debouncedQuery?.trim()) {
                searchParams.set('query', debouncedQuery.trim())
            }

            if (debouncedStatus?.trim()) {
                searchParams.set('status', debouncedStatus.trim())
            }

            if (debouncedRelationship?.trim()) {
                searchParams.set('relationship', debouncedRelationship.trim())
            }

            if (debouncedState?.trim()) {
                searchParams.set('state', debouncedState.trim())
            }

            if (debouncedCardId?.trim()) {
                searchParams.set('cardId', debouncedCardId.trim())
            }

            if (debouncedMemberId?.trim()) {
                searchParams.set('memberId', debouncedMemberId.trim())
            }

            if (debouncedBenefitPlan?.trim()) {
                searchParams.set('benefitPlan', debouncedBenefitPlan.trim())
            }

            if (debouncedMatchedUserEmail?.trim()) {
                searchParams.set('userEmail', debouncedMatchedUserEmail.trim())
            }

            if (onlyMatchedUsersFilter !== undefined) {
                searchParams.set('onlyMatchedUsers', onlyMatchedUsersFilter)
            }

            if (debouncedName?.trim()) {
                searchParams.set('name', debouncedName.trim())
            }

            if (debouncedBirthDate?.trim()) {
                searchParams.set('birthDate', debouncedBirthDate.trim())
            }

            groupsFilter.forEach(groupNumber => {
                searchParams.append('groups', String(groupNumber))
            })

            if (sort?.field) {
                searchParams.set('sort', `${sort.direction === 'asc' ? '' : '-'}${sort.field as string}`)
            }

            return http.getRequest<ApiResponse>(`/admin/portal-index?${searchParams.toString()}`)
        },
        placeholderData: keepPreviousData,
    })

    const handleCopyPatientInfo = useCallback(async (patient: PortalPatient) => {
        if (!patient.group.number || !patient.card_id) {
            toast.error('Patient info is incomplete', {position: 'top-right', duration: 2000})

            return
        }

        try {
            const nameParts = patient.name.split(' ')
            const firstName = nameParts.at(0) ?? 'N/A'
            const lastName = nameParts.at(-1) ?? 'N/A'
            const birthDate = patient.birth_date ?? 'N/A'

            const patientInfo = `First Name: ${firstName}\nLast Name: ${lastName}\nDate of Birth: ${birthDate}\nMember ID: ${patient.card_id}\nGroup Number: ${patient.group.number}`

            await navigator.clipboard.writeText(patientInfo)

            toast.success('Patient info copied to clipboard', {position: 'top-right', duration: 2000})
        } catch (error) {
            console.error('Failed to copy:', error)
        }
    }, [])

    const columns = useMemo<Array<Table.Column<PortalPatient>>>(() => [
        {
            name: 'id',
            header: 'ID',
            gridArea: '130px',
            getValue: (row) => {
                const firstIdPart = row.id.slice(0, 4)
                const lastIdPart = row.id.slice(-4)

                return (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '0.5rem'
                    }}>
                        {`${firstIdPart}...${lastIdPart}`}

                        <Copyable
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            notificationOptions={{ position: 'top-right', duration: 2000 }}
                            value={row.id}
                        >
                            <MdContentCopy size={14} style={{ color: '#666666' }} />
                        </Copyable>
                    </div>
                )
            }
        },
        {
            name: 'card_id',
            gridArea: 'max-content',
            header: (
                <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                }}>
                    <span style={{
                        fontWeight: '500'
                    }}>
                        Card ID
                    </span>
                    <MdInfoOutline
                        size={16}
                        style={{
                            color: '#9ca3af',
                            cursor: 'help'
                        }}
                        title="This shows the member numbers printed on ID cards. These may differ from the member numbers stored in Lucent Health systems."
                    />
                </span>
            ),
        },
        {
            name: 'name',
            header: 'Name',
            gridArea: 'max-content',
            isSortable: true,
        },
        {
            name: 'birth_date',
            header: 'Birth Date',
            gridArea: '120px',
            getValue: (row) => {
                if (!row.birth_date) {
                    return <span style={{ color: '#9ca3af' }}>N/A</span>
                }

                return <FriendlyDate date={row.birth_date} />
            }
        },
        {
            name: 'member_id',
            header: 'Member ID',
            gridArea: 'max-content',
        },
        {
            name: 'group',
            header: 'Group',
            gridArea: 'max-content',
            getValue: (row) => {
                const groupNumber = row.group.number
                const groupName = row.group.name

                return (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        flexWrap: 'wrap'
                    }}>
                        <span style={{
                            fontWeight: '500'
                        }}>{groupNumber}</span>
                        <span style={{
                            color: '#9ca3af'
                        }}>{groupName}</span>
                    </div>
                )
            }
        },
        {
            name: 'state',
            header: 'State',
            gridArea: '100px',
            getValue: (row) => {
                if (!row.state) {
                    return (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%'
                        }}>
                            <span style={{
                                color: '#9ca3af'
                            }}>N/A</span>
                        </div>
                    )
                }

                return (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        padding: '0.125rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor: '#f3f4f6',
                        color: '#1f2937'
                    }}>
                        {row.state}
                    </span>
                )
            }
        },
        {
            name: 'relationship',
            header: (
                <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                }}>
                    <span style={{
                        fontWeight: '500'
                    }}>
                        Relationship
                    </span>
                    <MdInfoOutline
                        size={16}
                        style={{
                            color: '#9ca3af',
                            cursor: 'help'
                        }}
                        title="Click the value to see all related family members."
                    />
                </span>
            ),
            gridArea: '160px',
            getValue: (row) => {
                const isEnrollee = row.relationship === 'enrollee'

                return (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <span
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()

                                setMemberIdFilter(row.participant_id)
                                setPage(0)
                            }}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '80%',
                                padding: '0.125rem 0.5rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                backgroundColor: isEnrollee ? '#dbeafe' : '#f3e8ff',
                                color: isEnrollee ? '#1e40af' : '#6b21a8',
                                cursor: 'pointer',
                                transition: 'border-color 100ms ease-in-out'
                            }}
                        >
                            {row.relationship.charAt(0).toUpperCase() + row.relationship.slice(1)}
                        </span>

                    </div>
                )
            }
        },
        {
            name: 'status',
            header: (
                <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                }}>
                    <span style={{
                        fontWeight: '500'
                    }}>
                        Status
                    </span>
                    <MdInfoOutline
                        size={16}
                        style={{
                            color: '#9ca3af',
                            cursor: 'help'
                        }}
                        title="Indicates whether the member's coverage is currently active."
                    />
                </span>
            ),
            gridArea: '110px',
            getValue: (row) => {
                const isActive = row.active

                return (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        padding: '0.125rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
                        color: isActive ? '#166534' : '#991b1b'
                    }}>
                        {isActive ? 'Active' : 'Terminated'}
                    </span>
                )
            }
        },
        {
            name: 'benefit_plans',
            header: 'Plans',
            gridArea: 'max-content',
            getValue: (row) => {
                if (row.benefit_plans.length === 0) {
                    return <span style={{ color: '#9ca3af' }}>N/A</span>
                }

                return row.benefit_plans.join(', ')
            }
        },
        {
            name: 'has_matched_users',
            header: 'Matched',
            gridArea: '110px',
            getValue: (row) => {
                const hasMatchedUsers = row.matched_users.length > 0

                return (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        padding: '0.125rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor: hasMatchedUsers ? '#dcfce7' : '#fee2e2',
                        color: hasMatchedUsers ? '#166534' : '#991b1b'
                    }}>
                        {hasMatchedUsers ? 'Yes' : 'No'}
                    </span>
                )
            }
        },
        {
            name: 'matched_users',
            header: 'Users',
            getValue: (row) => {
                if (row.matched_users.length === 0) {
                    return <span style={{ color: '#9ca3af' }}>N/A</span>
                }

                return row.matched_users.map(user => user.email).join(', ')
            }
        },
        {
            name: 'actions',
            header: null,
            gridArea: '50px',
            getValue: (row) => {
                return (
                    <ContextMenuButton
                        actions={[
                            {
                                id: 'copy-patient-info',
                                icon: MdContentCopy,
                                label: 'Copy Patient Info',
                                onClick: () => {
                                    handleCopyPatientInfo(row)
                                }
                            }
                        ]}
                        onClose={() => ({})}
                    />
                )
            }
        }
    ], [handleCopyPatientInfo])

    const pagination = useMemo(() => ({
        page,
        pageSize,
        totalFiltered: query.data?.data?.meta?.total ?? 0,
        totalUnfiltered: query.data?.data?.meta?.total ?? 0,
    }), [page, pageSize, query.data?.data?.meta?.total])

    return (
        <div>
            <PageHeader>
                <h1>Lucent Index</h1>
                <SearchInput
                    value={searchQuery}
                    placeholder='Search by patient info'
                    onChange={event => {
                        setSearchQuery(event.target.value)
                        setPage(0)
                    }}
                />
            </PageHeader>

            <Table.Table
                data={query.data?.data?.patients ?? []}
                queryStatus={query.status}
                fetchStatus={query.fetchStatus}
                error={query.error as Error | undefined}
                noDataPlaceholder='No patients to display'
                columns={columns}
                sort={sort}
                onSortChange={(field, direction) => setSort({field: field as keyof PortalPatient, direction})}
                pagination={pagination}
                onPageChange={(value: number) => setPage(value - 1)}
                onPageSizeChange={(value: number) => {
                    setPageSize(value);
                    setPage(0)
                }}
                filters={[
                    {
                        name: 'status',
                        value: statusFilter ? [statusFilter] : [],
                        radio: true,
                        hideSearch: true,
                        options: [
                            {label: 'Active', value: 'active'},
                            {label: 'Terminated', value: 'terminated'},
                        ]
                    },
                    {
                        name: 'relationship',
                        value: relationshipFilter ? [relationshipFilter] : [],
                        radio: true,
                        hideSearch: true,
                        options: [
                            {label: 'Enrollee', value: 'enrollee'},
                            {label: 'Dependent', value: 'dependent'},
                        ]
                    },
                    {
                        name: 'state',
                        value: stateFilter ? [stateFilter] : [],
                        render: ({onChange, value = []}) => {
                            const currentValue = (value?.[0] as string) || ''

                            return (
                                <div style={{padding: '0.5rem'}}>
                                    <input
                                        autoFocus
                                        type='text'
                                        value={currentValue}
                                        placeholder='State'
                                        onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                                        style={{width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd'}}
                                    />
                                </div>
                            )
                        },
                        clearButton: ({onChange, value = []}) => {
                            const currentValue = value?.[0] as string

                            if (!currentValue || currentValue.trim() === '') {
                                return null
                            }

                            return (
                                <button
                                    type='button'
                                    style={clearButtonStyle}
                                    onClick={() => {
                                        onChange([])
                                    }}
                                >
                                    Clear filter
                                </button>
                            )
                        },
                        isActive: stateFilter !== undefined && stateFilter.trim() !== ''
                    },
                    {
                        name: 'card_id',
                        value: cardIdFilter ? [cardIdFilter] : [],
                        render: ({onChange, value = []}) => {
                            const currentValue = (value?.[0] as string) || ''

                            return (
                                <div style={{padding: '0.5rem'}}>
                                    <input
                                        autoFocus
                                        type='text'
                                        value={currentValue}
                                        placeholder='Card ID'
                                        onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                                        style={{width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd'}}
                                    />
                                </div>
                            )
                        },
                        clearButton: ({onChange, value = []}) => {
                            const currentValue = value?.[0] as string

                            if (!currentValue || currentValue.trim() === '') {
                                return null
                            }

                            return (
                                <button
                                    type='button'
                                    style={clearButtonStyle}
                                    onClick={() => {
                                        onChange([])
                                    }}
                                >
                                    Clear filter
                                </button>
                            )
                        },
                        isActive: cardIdFilter !== undefined && cardIdFilter.trim() !== ''
                    },
                    {
                        name: 'member_id',
                        value: memberIdFilter ? [memberIdFilter] : [],
                        render: ({onChange, value = []}) => {
                            const currentValue = (value?.[0] as string) || ''

                            return (
                                <div style={{padding: '0.5rem'}}>
                                    <input
                                        autoFocus
                                        type='text'
                                        value={currentValue}
                                        placeholder='Member ID'
                                        onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                                        style={{width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd'}}
                                    />
                                </div>
                            )
                        },
                        clearButton: ({onChange, value = []}) => {
                            const currentValue = value?.[0] as string

                            if (!currentValue || currentValue.trim() === '') {
                                return null
                            }

                            return (
                                <button
                                    type='button'
                                    style={clearButtonStyle}
                                    onClick={() => {
                                        onChange([])
                                    }}
                                >
                                    Clear filter
                                </button>
                            )
                        },
                        isActive: memberIdFilter !== undefined && memberIdFilter.trim() !== ''
                    },
                    {
                        name: 'benefit_plans',
                        value: benefitPlanFilter ? [benefitPlanFilter] : [],
                        render: ({onChange, value = []}) => {
                            const currentValue = (value?.[0] as string) || ''

                            return (
                                <div style={{padding: '0.5rem'}}>
                                    <input
                                        autoFocus
                                        type='text'
                                        value={currentValue}
                                        placeholder='Plan'
                                        onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                                        style={{width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd'}}
                                    />
                                </div>
                            )
                        },
                        clearButton: ({onChange, value = []}) => {
                            const currentValue = value?.[0] as string

                            if (!currentValue || currentValue.trim() === '') {
                                return null
                            }

                            return (
                                <button
                                    type='button'
                                    style={clearButtonStyle}
                                    onClick={() => {
                                        onChange([])
                                    }}
                                >
                                    Clear filter
                                </button>
                            )
                        },
                        isActive: benefitPlanFilter !== undefined && benefitPlanFilter.trim() !== ''
                    },
                    {
                        name: 'matched_users',
                        value: matchedUserEmailFilter ? [matchedUserEmailFilter] : [],
                        render: ({onChange, value = []}) => {
                            const currentValue = (value?.[0] as string) || ''

                            return (
                                <div style={{padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', borderBottom: '1px solid #eaeaea'}}>
                                    <input
                                        autoFocus
                                        type='text'
                                        value={currentValue}
                                        placeholder='User email'
                                        onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                                        style={{width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd'}}
                                    />
                                </div>
                            )
                        },
                        clearButton: ({onChange, value = []}) => {
                            const currentValue = value?.[0] as string

                            if (!currentValue || currentValue.trim() === '') {
                                return null
                            }

                            return (
                                <button
                                    type='button'
                                    style={clearButtonStyle}
                                    onClick={() => {
                                        onChange([])
                                    }}
                                >
                                    Clear filter
                                </button>
                            )
                        },
                        isActive: matchedUserEmailFilter !== undefined && matchedUserEmailFilter.trim() !== ''
                    },
                    {
                        name: 'name',
                        value: nameFilter ? [nameFilter] : [],
                        render: ({onChange, value = []}) => {
                            const currentValue = (value?.[0] as string) || ''

                            return (
                                <div style={{padding: '0.5rem'}}>
                                    <input
                                        autoFocus
                                        type='text'
                                        value={currentValue}
                                        placeholder='Name'
                                        onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                                        style={{width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd'}}
                                    />
                                </div>
                            )
                        },
                        clearButton: ({onChange, value = []}) => {
                            const currentValue = value?.[0] as string

                            if (!currentValue || currentValue.trim() === '') {
                                return null
                            }

                            return (
                                <button
                                    type='button'
                                    style={clearButtonStyle}
                                    onClick={() => {
                                        onChange([])
                                    }}
                                >
                                    Clear filter
                                </button>
                            )
                        },
                        isActive: nameFilter !== undefined && nameFilter.trim() !== ''
                    },
                    {
                        name: 'birth_date',
                        value: birthDateFilter ? [birthDateFilter] : [],
                        render: ({onChange, value = []}) => {
                            const currentValue = (value?.[0] as string) || ''

                            return (
                                <div style={{padding: '0.5rem'}}>
                                    <input
                                        autoFocus
                                        type='date'
                                        lang='en-US'
                                        value={currentValue}
                                        placeholder='Birth Date'
                                        onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                                        style={{width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd'}}
                                    />
                                </div>
                            )
                        },
                        clearButton: ({onChange, value = []}) => {
                            const currentValue = value?.[0] as string

                            if (!currentValue || currentValue.trim() === '') {
                                return null
                            }

                            return (
                                <button
                                    type='button'
                                    style={clearButtonStyle}
                                    onClick={() => {
                                        onChange([])
                                    }}
                                >
                                    Clear filter
                                </button>
                            )
                        },
                        isActive: birthDateFilter !== undefined && birthDateFilter.trim() !== ''
                    },
                    {
                        name: 'group',
                        value: groupsFilter,
                        render: ({onChange, value = []}) => <GroupsFilter onChange={onChange} value={value as string[]} />,
                        clearButton: ({onChange, value = []}) => {
                            const currentValue = value as string[]

                            if (!currentValue || currentValue.length === 0) {
                                return null
                            }

                            return (
                                <button
                                    type='button'
                                    style={clearButtonStyle}
                                    onClick={() => {
                                        onChange([])
                                    }}
                                >
                                    Clear filter
                                </button>
                            )
                        },
                        isActive: groupsFilter.length > 0
                    },
                    {
                        name: 'has_matched_users',
                        value: onlyMatchedUsersFilter ? [onlyMatchedUsersFilter] : [],
                        radio: true,
                        hideSearch: true,
                        options: [
                            {label: 'Matched', value: 'true'},
                            {label: 'Unmatched', value: 'false'},
                        ]
                    },
                ]}
                onFilterChange={(field, values) => {
                    const first = values?.[0] as string

                    if (field === 'status') {
                        setStatusFilter(first)
                    }

                    if (field === 'relationship') {
                        setRelationshipFilter(first)
                    }

                    if (field === 'state') {
                        setStateFilter(first)
                    }

                    if (field === 'card_id') {
                        setCardIdFilter(first)
                    }

                    if (field === 'member_id') {
                        setMemberIdFilter(first)
                    }

                    if (field === 'benefit_plans') {
                        setBenefitPlanFilter(first)
                    }

                    if (field === 'matched_users') {
                        setMatchedUserEmailFilter(first)
                    }

                    if (field === 'name') {
                        setNameFilter(first)
                    }

                    if (field === 'birth_date') {
                        setBirthDateFilter(first)
                    }

                    if (field === 'group') {
                        setGroupsFilter(values as string[])
                    }

                    if (field === 'has_matched_users') {
                        setOnlyMatchedUsersFilter(first)
                    }

                    setPage(0)
                }}
                onFiltersClear={() => {
                    setStatusFilter(undefined)
                    setRelationshipFilter(undefined)
                    setStateFilter(undefined)
                    setCardIdFilter(undefined)
                    setMemberIdFilter(undefined)
                    setBenefitPlanFilter(undefined)
                    setMatchedUserEmailFilter(undefined)
                    setOnlyMatchedUsersFilter(undefined)
                    setNameFilter(undefined)
                    setBirthDateFilter(undefined)
                    setGroupsFilter([])

                    setPage(0)
                }}
            />
        </div>
    )
}

const GroupsFilter = (props: { onChange: (value: string[]) => void, value: string[] }) => {
    const [groupSearchQuery, setGroupSearchQuery] = useState<string>('')
    const [debouncedGroupSearch] = useDebounceValue(groupSearchQuery, 400)

    const {data: groupsData, isPending} = useQuery<GroupsApiResponse>({
        queryKey: ['lucent-index-groups', debouncedGroupSearch?.trim() || ''],
        queryFn: async () => {
            const searchParams = new URLSearchParams()

            if (debouncedGroupSearch?.trim()) {
                searchParams.set('query', debouncedGroupSearch.trim())
            }

            return http.getRequest<GroupsApiResponse>(
                `/admin/portal-index/groups?${searchParams.toString()}`
            )
        },

        enabled: true,
        staleTime: 5 * 60 * 1000,
    })

    const groups = groupsData?.data?.groups ?? []

    const checkboxStyle = `
        .groups-filter-checkbox {
            position: relative;
        }
        .groups-filter-checkbox::before {
            content: "";
            width: 0.55rem;
            height: 0.55rem;
            transform: scale(0);
            box-shadow: inset 1rem 1rem #fff;
            transform-origin: center center;
            clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
            position: absolute;
            top: 50%;
            left: 50%;
            margin-top: -0.275rem;
            margin-left: -0.275rem;
            transition: transform 100ms ease-in-out;
        }
        .groups-filter-checkbox:checked::before {
            transform: scale(1);
        }
    `

    return (
        <>
            <style>{checkboxStyle}</style>
            <div style={{ width: '400px' }}>
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                backgroundColor: '#fff',
                zIndex: 10,
                width: groups.length > 0 ? '95%' : '100%'
            }}>
                <div style={{ padding: '0.5rem' }}>
                    <input
                        autoFocus
                        type='text'
                        name='group-search-query'
                        placeholder='Group number or/and name'
                        onChange={(e) => setGroupSearchQuery(e.target.value)}
                        value={groupSearchQuery}
                        style={{
                            border: '1px solid #eaeaea',
                            borderRadius: '0.5rem',
                            width: '100%',
                            height: '40px',
                            padding: '0.5rem',
                            zIndex: 1
                        }}
                    />
                </div>
            </div>

            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                marginTop: '3rem',
                padding: '0.5rem'
            }}>
                {groups.length === 0 && !isPending && (
                    <div style={{
                        padding: '1rem',
                        textAlign: 'center',
                        color: '#4b5563'
                    }}>
                        {debouncedGroupSearch?.trim() ? 'No groups found' : 'Type to search groups'}
                    </div>
                )}

                {isPending && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <span style={{
                            color: '#6b7280',
                            fontSize: '0.875rem'
                        }}>Loading groups...</span>
                    </div>
                )}

                {groups.length > 0 && !isPending && groups.map((group, index) => {
                    const isSelected = props.value.includes(group.number)

                    return (
                        <div
                            key={group.number}
                            style={{
                                padding: '0.5rem',
                                borderBottom: index < groups.length - 1 ? '1px solid #eaeaea' : 'none'
                            }}
                        >
                            <label style={{
                                fontSize: '0.785rem',
                                display: 'grid',
                                gridTemplateColumns: '1rem auto',
                                gridAutoFlow: 'column',
                                gap: '0.5rem',
                                cursor: 'pointer',
                                position: 'relative'
                            }}>
                                <input
                                    type='checkbox'
                                    checked={isSelected}
                                    onChange={() => props.onChange(isSelected ? props.value.filter(value => value !== group.number) : [...props.value, group.number])}
                                    className='groups-filter-checkbox'
                                    style={{
                                        appearance: 'none',
                                        backgroundColor: isSelected ? '#7728ff' : '#fff',
                                        margin: 0,
                                        font: 'inherit',
                                        color: '#888',
                                        width: '1rem',
                                        height: '1rem',
                                        border: isSelected ? 'transparent' : '2px solid #888',
                                        borderRadius: '0.15rem',
                                        display: 'grid',
                                        placeContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'border-color 100ms ease-in-out, background-color 100ms ease-in-out',
                                        position: 'relative'
                                    }}
                                />

                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    flexWrap: 'wrap'
                                }}>
                                    <span style={{
                                        fontWeight: '500'
                                    }}>
                                        {group.number}
                                    </span>

                                    <span style={{
                                        color: '#9ca3af'
                                    }}>
                                        {group.name}
                                    </span>
                                </div>
                            </label>
                        </div>
                    )
                })}
            </div>
        </div>
        </>
    )
}

export default PortalIndex
